import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import type { FinanceColumn, FinanceStudentRow } from "./financeStudentsColumns";

const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export function financeStudentsFileName(today: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `financieel-leerlingen-${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}.xlsx`;
}

/** Builds the workbook with exactly the columns and rows the user selected, in that order. */
export function buildFinanceStudentsWorkbook(
  columns: FinanceColumn[],
  rows: FinanceStudentRow[],
  sheetName: string,
): ExcelJS.Workbook {
  const workbook = new ExcelJS.Workbook();
  workbook.created = new Date();
  const sheet = workbook.addWorksheet(sheetName.slice(0, 31) || "Sheet1", {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  sheet.columns = columns.map((column) => ({
    header: column.label,
    key: column.key,
    width: Math.min(45, Math.max(12, column.label.length + 2)),
  }));
  rows.forEach((row) => {
    const added = sheet.addRow(Object.fromEntries(columns.map((column) => [column.key, column.value(row)])));
    if (row.status === "WITHDRAWN") {
      added.font = { italic: true, color: { argb: "FF6B7280" } };
    }
  });

  const header = sheet.getRow(1);
  header.font = { bold: true };
  header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E7FF" } };
  if (columns.length > 0) {
    sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: columns.length } };
  }
  sheet.columns.forEach((column) => {
    let longest = String(column.header ?? "").length;
    column.eachCell?.({ includeEmpty: false }, (cell) => {
      longest = Math.max(longest, String(cell.value ?? "").length);
    });
    column.width = Math.min(45, Math.max(12, longest + 2));
  });
  return workbook;
}

export async function downloadFinanceStudentsExcel(
  columns: FinanceColumn[],
  rows: FinanceStudentRow[],
  sheetName: string,
): Promise<void> {
  const workbook = buildFinanceStudentsWorkbook(columns, rows, sheetName);
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: XLSX_TYPE }), financeStudentsFileName());
}
